import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Loader from "../../components/ui/Loader";
import { useGetProfileQuery } from "../../store/api/authApi";
import { useGetConversationsQuery } from "../../store/api/conversationsApi";
import { useListPropertiesQuery } from "../../store/api/propertiesApi";
import { useListViewingsQuery } from "../../store/api/propertyViewingsApi";

const AgentDashboard: React.FC = () => {
	const navigate = useNavigate();
	const { data: user, isLoading: isLoadingUser } = useGetProfileQuery();

	// Fetch agent's data
	const { data: propertiesData } = useListPropertiesQuery({ page_size: 100 });
	const { data: viewingsData } = useListViewingsQuery({
		page_size: 100,
		ordering: "-viewing_date",
	});
	const { data: conversationsData } = useGetConversationsQuery();

	// Filter data for this agent - show only available properties (for sale/rent)
	const agentProperties = useMemo(() => {
		if (!propertiesData?.results) return [];
		// Only show properties that are available (not occupied or in maintenance)
		// Properties are available for sale/rent if they have purchase_price or rent_amount
		return propertiesData.results.filter(
			(p) =>
				p.status === "available" &&
				(p.purchase_price != null ||
					p.rent_amount != null ||
					p.monthly_rent != null),
		);
	}, [propertiesData]);

	const agentViewings = useMemo(() => {
		if (!viewingsData?.results || !user?.id) return [];
		return viewingsData.results.filter((v: any) => v.agent === user.id);
	}, [viewingsData, user]);

	const upcomingViewings = useMemo(() => {
		const now = new Date();
		return agentViewings
			.filter((v: any) => {
				const viewingDate = new Date(v.viewing_date);
				return (
					viewingDate >= now &&
					v.status !== "cancelled" &&
					v.status !== "completed"
				);
			})
			.slice(0, 5);
	}, [agentViewings]);

	const stats = useMemo(
		() => ({
			totalProperties: agentProperties.length,
			upcomingViewings: upcomingViewings.length,
			totalViewings: agentViewings.length,
			completedViewings: agentViewings.filter(
				(v: any) => v.status === "completed",
			).length,
			messages:
				(conversationsData as any)?.results?.length ||
				(Array.isArray(conversationsData) ? conversationsData.length : 0),
		}),
		[agentProperties, upcomingViewings, agentViewings, conversationsData],
	);

	if (isLoadingUser) {
		return (
			<Loader size="lg" showText text="Loading dashboard..." className="h-64" />
		);
	}

	return (
		<div className="w-full max-w-7xl mx-auto">
			{/* Page Header */}
			<div className="mb-6">
				<h1 className="text-2xl font-bold text-foreground">
					Welcome, {user?.first_name} {user?.last_name}
				</h1>
				<p className="text-muted-foreground mt-1">Agent Dashboard</p>
			</div>

			{/* Stats Cards */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
				<div className="bg-card border border-border rounded-lg p-6 card-shadow">
					<div className="flex items-center justify-between mb-2">
						<Icon name="Home" size={24} className="text-primary" />
						<span className="text-2xl font-bold text-foreground">
							{stats.totalProperties}
						</span>
					</div>
					<h3 className="font-semibold text-foreground mb-1">Properties</h3>
					<p className="text-muted-foreground text-sm">
						Properties available for sale/rent
					</p>
				</div>

				<div className="bg-card border border-border rounded-lg p-6 card-shadow">
					<div className="flex items-center justify-between mb-2">
						<Icon name="Calendar" size={24} className="text-success" />
						<span className="text-2xl font-bold text-foreground">
							{stats.upcomingViewings}
						</span>
					</div>
					<h3 className="font-semibold text-foreground mb-1">
						Upcoming Viewings
					</h3>
					<p className="text-muted-foreground text-sm">
						Scheduled property viewings
					</p>
				</div>

				<div className="bg-card border border-border rounded-lg p-6 card-shadow">
					<div className="flex items-center justify-between mb-2">
						<Icon name="CheckCircle" size={24} className="text-primary" />
						<span className="text-2xl font-bold text-foreground">
							{stats.completedViewings}
						</span>
					</div>
					<h3 className="font-semibold text-foreground mb-1">Completed</h3>
					<p className="text-muted-foreground text-sm">
						Total completed viewings
					</p>
				</div>

				<div className="bg-card border border-border rounded-lg p-6 card-shadow">
					<div className="flex items-center justify-between mb-2">
						<Icon name="MessageSquare" size={24} className="text-warning" />
						<span className="text-2xl font-bold text-foreground">
							{stats.messages}
						</span>
					</div>
					<h3 className="font-semibold text-foreground mb-1">Messages</h3>
					<p className="text-muted-foreground text-sm">Active conversations</p>
				</div>
			</div>

			{/* Quick Actions */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
				<Button
					variant="outline"
					className="h-auto p-6 flex flex-col items-center space-y-3"
					onClick={() => navigate("/property-viewing-management")}
				>
					<Icon name="Calendar" size={32} className="text-primary" />
					<div className="text-center">
						<h3 className="font-semibold text-foreground mb-1">
							Manage Viewings
						</h3>
						<p className="text-sm text-muted-foreground">
							Schedule and track property viewings
						</p>
					</div>
				</Button>

				<Button
					variant="outline"
					className="h-auto p-6 flex flex-col items-center space-y-3"
					onClick={() => navigate("/sales-management")}
				>
					<Icon name="TrendingUp" size={32} className="text-primary" />
					<div className="text-center">
						<h3 className="font-semibold text-foreground mb-1">
							Sales Management
						</h3>
						<p className="text-sm text-muted-foreground">
							Manage property listings and sales
						</p>
					</div>
				</Button>

				<Button
					variant="outline"
					className="h-auto p-6 flex flex-col items-center space-y-3"
					onClick={() => navigate("/messages")}
				>
					<Icon name="MessageSquare" size={32} className="text-primary" />
					<div className="text-center">
						<h3 className="font-semibold text-foreground mb-1">Messages</h3>
						<p className="text-sm text-muted-foreground">
							Communicate with clients
						</p>
					</div>
				</Button>
			</div>

			{/* Upcoming Viewings */}
			{upcomingViewings.length > 0 && (
				<div className="bg-card border border-border rounded-lg p-6 card-shadow">
					<h3 className="text-lg font-semibold text-foreground mb-4">
						Upcoming Viewings
					</h3>
					<div className="space-y-3">
						{upcomingViewings.map((viewing: any) => (
							<div
								key={viewing.id}
								className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50"
							>
								<div>
									<p className="font-medium text-foreground">
										{viewing.property_name || "Property"}
									</p>
									<p className="text-sm text-muted-foreground">
										{viewing.client_name} •{" "}
										{new Date(viewing.viewing_date).toLocaleString()}
									</p>
								</div>
								<span
									className={`px-2 py-1 rounded-full text-xs font-medium ${
										viewing.status === "confirmed"
											? "bg-success/10 text-success"
											: viewing.status === "scheduled"
											? "bg-primary/10 text-primary"
											: "bg-warning/10 text-warning"
									}`}
								>
									{viewing.status}
								</span>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default AgentDashboard;
